/**
 * Field — label + hint + error wrapper for a form control (Claude Design
 * `.field`): a 6px-gap column, a 12/600 muted label, faint hints and a bold
 * danger error message.
 */
import { useId, type ReactElement, type ReactNode } from 'react';

import { cn } from './cn.js';

export interface FieldProps {
  label: string;
  hint?: string;
  error?: string;
  htmlFor?: string;
  className?: string;
  children: ReactNode;
}

export function Field({
  label,
  hint,
  error,
  htmlFor,
  className,
  children,
}: FieldProps): ReactElement {
  const generatedId = useId();
  const id = htmlFor ?? generatedId;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="flex items-center gap-1.5 text-xs font-semibold text-muted">
        {label}
      </label>
      {children}
      {error ? (
        <p role="alert" className="text-[11.5px] font-semibold text-danger">
          {error}
        </p>
      ) : hint ? (
        <p className="text-[11.5px] text-faint">{hint}</p>
      ) : null}
    </div>
  );
}
